// Real API client for the V2 contract subsystem. Mirrors the action
// vocabulary of ContractsV2Context's reducer so wiring stays mechanical:
// each reducer action becomes a method here, and the response is fed
// back into the reducer to update local state.
//
// Slice 1 covers create + read. Later slices add lifecycle, payment,
// delivery, revision, dispute methods.

import { apiClient } from './apiClient';
import type {
  ContractBundle,
  CreateContractRequest,
} from '../pages/Contracts/types';

interface BundleResponse {
  success: boolean;
  data?: { bundle: ContractBundle };
  error?: string;
}

interface BundleListResponse {
  success: boolean;
  data?: { bundles: ContractBundle[] };
  error?: string;
}

const BASE = '/api/v1/contracts';

interface UploadPdfResponse {
  success: boolean;
  data?: { ossKey: string; fileName: string; size: number };
  error?: string;
}

export const contractApiService = {
  async create(req: CreateContractRequest): Promise<{ success: true; bundle: ContractBundle } | { success: false; error: string }> {
    const res = await apiClient.post<BundleResponse>(BASE, req);
    if (res.success && res.data?.success && res.data.data?.bundle) {
      return { success: true, bundle: res.data.data.bundle };
    }
    return { success: false, error: res.data?.error || res.error || '合同创建失败' };
  },

  // Upload a custom contract PDF (upload mode). Returns the OSS key to
  // pass as customPdfOssKey when creating the contract.
  async uploadPdf(fileName: string, fileBase64: string): Promise<{ success: true; ossKey: string; fileName: string } | { success: false; error: string }> {
    const res = await apiClient.post<UploadPdfResponse>(`${BASE}/upload-pdf`, { fileName, fileBase64 });
    if (res.success && res.data?.success && res.data.data?.ossKey) {
      return { success: true, ossKey: res.data.data.ossKey, fileName: res.data.data.fileName };
    }
    return { success: false, error: res.data?.error || res.error || 'PDF 上传失败' };
  },

  // Stream a previously-uploaded PDF back and return a browser object
  // URL for in-page preview. Caller is responsible for revoking it.
  async previewUploadedPdf(ossKey: string, fileName?: string): Promise<{ success: true; objectUrl: string } | { success: false; error: string }> {
    const res = await apiClient.postBlob(`${BASE}/upload-pdf-preview`, { ossKey, fileName });
    if (res.success && res.data) {
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/pdf' });
      return { success: true, objectUrl: URL.createObjectURL(blob) };
    }
    return { success: false, error: (res as any).error || '无法预览合同文件' };
  },

  // ─────────────────────────────────────────────────────
  // Project share links (password-protected collaboration)
  // ─────────────────────────────────────────────────────

  async createShare(contractId: string, password: string): Promise<{ success: true; shareToken: string } | { success: false; error: string }> {
    const res = await apiClient.post<{ success: boolean; data?: { shareToken: string }; error?: string }>(`${BASE}/${contractId}/shares`, { password });
    if (res.success && res.data?.success && res.data.data?.shareToken) {
      return { success: true, shareToken: res.data.data.shareToken };
    }
    return { success: false, error: res.data?.error || res.error || '创建分享链接失败' };
  },

  async getShareMeta(shareToken: string): Promise<{ success: true; contractId: string; title: string; alreadyMember: boolean } | { success: false; error: string }> {
    const res = await apiClient.get<{ success: boolean; data?: { contractId: string; title: string; alreadyMember: boolean }; error?: string }>(`${BASE}/shares/${shareToken}`);
    if (res.success && res.data?.success && res.data.data) {
      return { success: true, ...res.data.data };
    }
    return { success: false, error: res.data?.error || res.error || '分享链接无效或已失效' };
  },

  async redeemShare(shareToken: string, password: string): Promise<{ success: true; contractId: string } | { success: false; error: string }> {
    const res = await apiClient.post<{ success: boolean; data?: { contractId: string }; error?: string }>(`${BASE}/shares/${shareToken}/redeem`, { password });
    if (res.success && res.data?.success && res.data.data?.contractId) {
      return { success: true, contractId: res.data.data.contractId };
    }
    return { success: false, error: res.data?.error || res.error || '加入项目失败' };
  },

  // Get a short-lived e签宝 download URL for the signed contract PDF.
  async getSignedPdfUrl(contractId: string): Promise<{ success: true; downloadUrl: string } | { success: false; error: string }> {
    const res = await apiClient.get<{ success: boolean; data?: { downloadUrl: string }; error?: string }>(`${BASE}/${contractId}/signed-pdf`);
    if (res.success && res.data?.success && res.data.data?.downloadUrl) {
      return { success: true, downloadUrl: res.data.data.downloadUrl };
    }
    return { success: false, error: res.data?.error || res.error || '获取合同 PDF 失败' };
  },

  async getBundle(contractId: string): Promise<{ success: true; bundle: ContractBundle } | { success: false; error: string }> {
    const res = await apiClient.get<BundleResponse>(`${BASE}/${contractId}`);
    if (res.success && res.data?.success && res.data.data?.bundle) {
      return { success: true, bundle: res.data.data.bundle };
    }
    return { success: false, error: res.data?.error || res.error || '合同加载失败' };
  },

  async listForUser(): Promise<{ success: true; bundles: ContractBundle[] } | { success: false; error: string }> {
    const res = await apiClient.get<BundleListResponse>(BASE);
    if (res.success && res.data?.success && res.data.data?.bundles) {
      return { success: true, bundles: res.data.data.bundles };
    }
    return { success: false, error: res.data?.error || res.error || '合同列表加载失败' };
  },

  // ─────────────────────────────────────────────────────
  // Slice 2 — lifecycle
  // ─────────────────────────────────────────────────────

  async accept(contractId: string): Promise<{ success: true; bundle: ContractBundle } | { success: false; error: string }> {
    const res = await apiClient.post<BundleResponse>(`${BASE}/${contractId}/accept`, {});
    if (res.success && res.data?.success && res.data.data?.bundle) {
      return { success: true, bundle: res.data.data.bundle };
    }
    return { success: false, error: res.data?.error || res.error || '接受合同失败' };
  },

  async reject(contractId: string, reason?: string): Promise<{ success: true; bundle: ContractBundle } | { success: false; error: string }> {
    const res = await apiClient.post<BundleResponse>(`${BASE}/${contractId}/reject`, { reason: reason || '' });
    if (res.success && res.data?.success && res.data.data?.bundle) {
      return { success: true, bundle: res.data.data.bundle };
    }
    return { success: false, error: res.data?.error || res.error || '拒绝合同失败' };
  },

  async refreshSigning(contractId: string): Promise<{ success: true; bundle: ContractBundle } | { success: false; error: string }> {
    const res = await apiClient.post<BundleResponse>(`${BASE}/${contractId}/refresh-signing`, {});
    if (res.success && res.data?.success && res.data.data?.bundle) {
      return { success: true, bundle: res.data.data.bundle };
    }
    return { success: false, error: res.data?.error || res.error || '刷新签署状态失败' };
  },

  async cancel(contractId: string): Promise<{ success: true; bundle: ContractBundle } | { success: false; error: string }> {
    const res = await apiClient.post<BundleResponse>(`${BASE}/${contractId}/cancel`, {});
    if (res.success && res.data?.success && res.data.data?.bundle) {
      return { success: true, bundle: res.data.data.bundle };
    }
    return { success: false, error: res.data?.error || res.error || '取消合同失败' };
  },

  // ─────────────────────────────────────────────────────
  // Slice 3 — payments
  // ─────────────────────────────────────────────────────

  async markPaymentPaid(contractId: string, paymentId: string): Promise<{ success: true; bundle: ContractBundle } | { success: false; error: string }> {
    const res = await apiClient.post<BundleResponse>(`${BASE}/${contractId}/payments/${paymentId}/mark-paid`, {});
    if (res.success && res.data?.success && res.data.data?.bundle) {
      return { success: true, bundle: res.data.data.bundle };
    }
    return { success: false, error: res.data?.error || res.error || '标记付款失败' };
  },

  async confirmPayment(contractId: string, paymentId: string): Promise<{ success: true; bundle: ContractBundle } | { success: false; error: string }> {
    const res = await apiClient.post<BundleResponse>(`${BASE}/${contractId}/payments/${paymentId}/confirm`, {});
    if (res.success && res.data?.success && res.data.data?.bundle) {
      return { success: true, bundle: res.data.data.bundle };
    }
    return { success: false, error: res.data?.error || res.error || '确认付款失败' };
  },

  // ─────────────────────────────────────────────────────
  // Slice 4 — milestone delivery + acceptance
  // ─────────────────────────────────────────────────────

  async submitDelivery(
    contractId: string,
    milestoneId: string,
    body: { assets: Array<{ type: string; label: string; fileUrl?: string; uploadedAt?: number }>; validityPassed?: boolean; validityIssues?: string[] },
  ): Promise<{ success: true; bundle: ContractBundle } | { success: false; error: string }> {
    const res = await apiClient.post<BundleResponse>(
      `${BASE}/${contractId}/milestones/${milestoneId}/submit-delivery`, body,
    );
    if (res.success && res.data?.success && res.data.data?.bundle) {
      return { success: true, bundle: res.data.data.bundle };
    }
    return { success: false, error: res.data?.error || res.error || '提交交付失败' };
  },

  async acceptMilestone(
    contractId: string,
    milestoneId: string,
  ): Promise<{ success: true; bundle: ContractBundle } | { success: false; error: string }> {
    const res = await apiClient.post<BundleResponse>(
      `${BASE}/${contractId}/milestones/${milestoneId}/accept`, {},
    );
    if (res.success && res.data?.success && res.data.data?.bundle) {
      return { success: true, bundle: res.data.data.bundle };
    }
    return { success: false, error: res.data?.error || res.error || '验收失败' };
  },

  // ─────────────────────────────────────────────────────
  // Slice 5 — revisions + changes
  // ─────────────────────────────────────────────────────

  async requestRevision(
    contractId: string,
    milestoneId: string,
    body: { category: 'detail' | 'invalid_delivery'; reason: string; revisionDueAt?: number },
  ): Promise<{ success: true; bundle: ContractBundle } | { success: false; error: string }> {
    const res = await apiClient.post<BundleResponse>(
      `${BASE}/${contractId}/milestones/${milestoneId}/revisions`, body,
    );
    if (res.success && res.data?.success && res.data.data?.bundle) {
      return { success: true, bundle: res.data.data.bundle };
    }
    return { success: false, error: res.data?.error || res.error || '提交修改请求失败' };
  },

  async proposeChange(
    contractId: string,
    body: { scopeDelta: string; feeDelta: number; additionalDays: number },
  ): Promise<{ success: true; bundle: ContractBundle } | { success: false; error: string }> {
    const res = await apiClient.post<BundleResponse>(`${BASE}/${contractId}/changes`, body);
    if (res.success && res.data?.success && res.data.data?.bundle) {
      return { success: true, bundle: res.data.data.bundle };
    }
    return { success: false, error: res.data?.error || res.error || '提交变更单失败' };
  },

  async agreeChange(
    contractId: string,
    changeId: string,
  ): Promise<{ success: true; bundle: ContractBundle } | { success: false; error: string }> {
    const res = await apiClient.post<BundleResponse>(`${BASE}/${contractId}/changes/${changeId}/agree`, {});
    if (res.success && res.data?.success && res.data.data?.bundle) {
      return { success: true, bundle: res.data.data.bundle };
    }
    return { success: false, error: res.data?.error || res.error || '同意变更失败' };
  },

  async rejectChange(
    contractId: string,
    changeId: string,
  ): Promise<{ success: true; bundle: ContractBundle } | { success: false; error: string }> {
    const res = await apiClient.post<BundleResponse>(`${BASE}/${contractId}/changes/${changeId}/reject`, {});
    if (res.success && res.data?.success && res.data.data?.bundle) {
      return { success: true, bundle: res.data.data.bundle };
    }
    return { success: false, error: res.data?.error || res.error || '拒绝变更失败' };
  },

  // ─────────────────────────────────────────────────────
  // Slice 6 — disputes
  // ─────────────────────────────────────────────────────

  async raiseDispute(
    contractId: string,
    reason: string,
  ): Promise<{ success: true; bundle: ContractBundle } | { success: false; error: string }> {
    const res = await apiClient.post<BundleResponse>(`${BASE}/${contractId}/disputes`, { reason });
    if (res.success && res.data?.success && res.data.data?.bundle) {
      return { success: true, bundle: res.data.data.bundle };
    }
    return { success: false, error: res.data?.error || res.error || '发起争议失败' };
  },

  // ─────────────────────────────────────────────────────
  // Post-completion — mutual reviews + invoice upload
  // ─────────────────────────────────────────────────────

  async submitReview(
    contractId: string,
    body: { rating: number; comment: string; dimensionRatings?: Record<string, number> },
  ): Promise<{ success: true; bundle: ContractBundle } | { success: false; error: string }> {
    const res = await apiClient.post<BundleResponse>(`${BASE}/${contractId}/reviews`, body);
    if (res.success && res.data?.success && res.data.data?.bundle) {
      return { success: true, bundle: res.data.data.bundle };
    }
    return { success: false, error: res.data?.error || res.error || '提交评价失败' };
  },

  async submitInvoice(
    contractId: string,
    body: { fileUrl: string; fileName: string; amount?: number; note?: string },
  ): Promise<{ success: true; bundle: ContractBundle } | { success: false; error: string }> {
    const res = await apiClient.post<BundleResponse>(`${BASE}/${contractId}/invoices`, body);
    if (res.success && res.data?.success && res.data.data?.bundle) {
      return { success: true, bundle: res.data.data.bundle };
    }
    return { success: false, error: res.data?.error || res.error || '上传发票失败' };
  },
};
